/*---------------------------------------------------------------------------------------------
* Copyright (c) Bentley Systems, Incorporated. All rights reserved.
* See LICENSE.md in the project root for license terms and full copyright notice.
*--------------------------------------------------------------------------------------------*/
import { useMemo } from "react";
import { Table } from "@itwin/itwinui-react";
import styles from "./PackageVersions.module.css";

interface PackageData extends Record<string, string | undefined> {
  name: string;
  version: string;
  latestVersion?: string;
}

const sortPackages = (packages: PackageData[]) => {
  const sortedPackages = [...packages];
  sortedPackages.sort((a, b) => {
    const nameA = a.name.toUpperCase(); // ignore upper and lowercase
    const nameB = b.name.toUpperCase(); // ignore upper and lowercase
    if (nameA < nameB) {
      return -1;
    }
    if (nameA > nameB) {
      return 1;
    }

    // names must be equal
    return 0;
  });
  return sortedPackages;
};

export const PackageVersions = ({ packages, className }: { packages?: PackageData[], className?: string }) => {

  const columns = useMemo(() => [
    {
      id: "name",
      Header: "Package Name",
      accessor: "name",
    },
    {
      id: "version",
      Header: "Version",
      accessor: "version",
      width: 100,
    }
  ], []);

  const rows = useMemo(() => {
    if (!packages)
      return [];

    const data = sortPackages(packages);
    return data;
  }, [packages]);

  return (
    <div className={[styles.container, className].join(" ")}>
      <Table<PackageData>
        className={[styles.versionTable].join(" ")}
        density="extra-condensed"
        columns={columns}
        data={rows}
        emptyTableContent="No Data"
      />
    </div>
  );
};
